// @flow
/* -----------------Globals--------------- */
import React from 'react';

/* -----------------3rd-party--------------- */
import clsx from 'clsx';

/* -----------------UI--------------- */
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

/* -----------------Markdown--------------- */
import ReactMarkdown from 'react-markdown';
import subSuper from 'remark-sub-super';
import gfm from 'remark-gfm';

/* -----------------Child components--------------- */
import ZoomImage from './ZoomImage';

/* -----------------Utilities--------------- */
import rules from 'common/markdown/rulesWeb';
import Link from './link';

/* -----------------Styles--------------- */
const styles = theme => ({
  crossedOut: {},
  markdown: {
    wordBreak: 'break-word',
    '& p:first-of-type': {
      marginTop: 0,
    },
    '& p:last-of-type': {
      marginBottom: 0,
    },
    '& *': {
      wordBreak: 'break-word',
    },
    '&$crossedOut': {
      '& *': {
        textDecoration: 'line-through',
      },
    },
  },
  imageContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  tableContainer: {
    width: 'fit-content',
    minWidth: '50%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderBottom: 0,
    borderRadius: theme.shape.borderRadius,

  },
  tableCellHead: {
    fontWeight: 600,
  },
  imageThumbnailFullWidth: {
    height: 'auto',
    maxWidth: '100%',
    width: '100%',
  },
  link: {
    color: theme.palette.type === 'light'
      ? theme.palette.secondary.dark
      : theme.palette.secondary.main,
  },

});
/* -----------------Type declarations--------------- */
type Classes = {
  markdown: string,
  image: string,
  link: string,
  crossedOut: string,
  imageContainer: string,
  tableContainer: string,
  tableCellHead: string
}

/* -----------------Props--------------- */
type Props = {
  classes: Classes,
  crossedOut?: boolean,
  source: string,
  customStyles: *,
  variant?: 'term' | 'fact' | 'description' | 'stem' | 'answer' | 'explanation' | 'blog',
  customList?: ?boolean
};

/* -----------------Component--------------- */
const RenderMarkdown = (props: Props) => {
  const {
    classes,
    source,
    crossedOut = false,
    customStyles = {},
    variant = 'term',
    customList,
  } = props;

  const {
    imageCustomStyle,
    thumbnailStyle,
    imageContainer,
  } = customStyles;

  // We need to pass as object directly because of className inheritance issues

  const renderImage = (renderProps) => {
    const { src } = renderProps;
    const uri = {
      url: !src.startsWith('http') ? `https:${src}` : src,
    };
    return (
      <div
        className={classes.imageContainer}
        style={imageContainer}
      >
        <ZoomImage
          image={uri}
          thumbnailStyle={thumbnailStyle}
          customStyle={imageCustomStyle}
        />
      </div>
    );
  };

  const renderTable = (renderProps) => {
    const { children } = renderProps;
    return (
      <TableContainer classes={{ root: classes.tableContainer }}>
        <Table>
          {children}
        </Table>
      </TableContainer>
    );
  };

  const renderTableHead = (renderProps) => {
    const { children } = renderProps;
    return <TableHead>{children}</TableHead>;
  };

  const renderTableBody = (renderProps) => {
    const { children } = renderProps;
    return <TableBody>{children}</TableBody>;
  };

  const renderTableRow = (renderProps) => {
    const { children } = renderProps;
    return <TableRow hover>{children}</TableRow>;
  };

  const renderTableCell = (renderProps) => {
    const { children } = renderProps;
    return (
      <TableCell
        classes={{ head: classes.tableCellHead }}
      >
        {children}
      </TableCell>
    );
  };

  const renderLink = (renderProps) => {
    const { href, children } = renderProps;
    const target = href.startsWith('/') ? '_self' : '_blank';
    return (
      <Link
        href={href}
        target={target}
        className={classes.link}
      >
        {children}
      </Link>
    );
  };

  const renderList = ({ children }) => children.map((item, index) => (
    <Box
      textAlign="left"
      key={index.toString()}
      fontSize="0.8em"
      fontWeight={300}
      my={0.5}
    >
      {item}
    </Box>
  ));
  const customRenderers = {
    image: renderImage,
    sub: 'sub',
    sup: 'sup',
    link: renderLink,
    table: renderTable,
    tableHead: renderTableHead,
    tableBody: renderTableBody,
    tableRow: renderTableRow,
    tableCell: renderTableCell,
    ...(customList ? {
      list: renderList,
    } : {}),
  };

  // Disable syntax based upon variant rules
  const { disallowedTypes } = rules[variant];
  return (
    <div className={clsx(classes.markdown, crossedOut && classes.crossedOut)}>
      <ReactMarkdown
        renderers={customRenderers}
        plugins={[subSuper, gfm]}
        escapeHtml={false}
        unwrapDisallowed
        source={source}
        linkTarget="_blank"
        disallowedTypes={disallowedTypes}
      />
    </div>
  );
};

export default withStyles(styles)(RenderMarkdown);
